(() => {
  /**
   * <redeem-slider>
   * Page-scoped horizontal carousel for the rewards page. Native overflow-x scroll.
   * Click nav scrolls by one viewport width (matches old slidesPerGroup === slidesPerView).
   */
  class RedeemSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-rd-track]");
      if (!this.track) return;

      this.prevBtn = this.querySelector("[data-rd-prev]");
      this.nextBtn = this.querySelector("[data-rd-next]");

      // Defensive: reset scroll position (RTL inheritance / iOS layout quirks).
      this.track.scrollLeft = 0;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.reduceMotionMq = window.matchMedia("(prefers-reduced-motion: reduce)");

      this.prevBtn?.addEventListener("click", () => this.scrollByPage(-1), { signal });
      this.nextBtn?.addEventListener("click", () => this.scrollByPage(1), { signal });
      this.track.addEventListener("scroll", () => this.updateButtonState(), { signal, passive: true });
      window.addEventListener("resize", () => this.updateButtonState(), { signal, passive: true });

      this.updateButtonState();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    scrollByPage(direction) {
      this.track.scrollBy({
        left: this.track.clientWidth * direction,
        behavior: this.reduceMotionMq.matches ? "auto" : "smooth",
      });
    }

    updateButtonState() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const noScroll = max <= 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= max - 1 || max <= 0;
      // When content fits without scrolling, hide both buttons entirely.
      this.prevBtn?.classList.toggle("is-hidden", noScroll);
      this.nextBtn?.classList.toggle("is-hidden", noScroll);
      this.prevBtn?.setAttribute("aria-disabled", String(atStart));
      this.nextBtn?.setAttribute("aria-disabled", String(atEnd));
    }
  }

  if (!window.customElements.get("redeem-slider")) {
    window.customElements.define("redeem-slider", RedeemSlider);
  }

  async function init() {
    const container = document.querySelector(".js-redeem-content");
    if (!container) return;

    container.addEventListener("click", onRedeemClick);
    container.addEventListener("click", onSwatchClick);
    container.addEventListener("click", onTabClick);

    try {
      const accountInfo = await window._erj_?.getAccountInfo?.();
      let codeConfigs = accountInfo?.codeConfigs || [];

      if (!codeConfigs.length) {
        container.replaceChildren(Object.assign(document.createElement("div"), { className: "redeem-list__empty", textContent: window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.rewards_empty || "No rewards available at the moment." }));
        return;
      }

      codeConfigs = await enrichCodeConfigs(codeConfigs);

      const grouped = groupConfigs(codeConfigs);
      const navLabels = {
        prev: container.dataset.labelPrev || "Previous",
        next: container.dataset.labelNext || "Next",
      };
      renderGroupedList(container, grouped, navLabels);
    } catch (e) {
      console.error("Reward Redeem List Error:", e);
      container.replaceChildren(Object.assign(document.createElement("div"), { className: "redeem-list__error", textContent: window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.rewards_error || "Failed to load rewards. Please try again later." }));
    }
  }

  async function enrichCodeConfigs(configs) {
    const fetchPromises = configs.map(async (config) => {
      if (config.configExtra?.type === "free_product") {
        const handle = config.configExtra?.products?.[0]?.handle;
        if (handle) {
          try {
            const response = await fetch(window._erj_.localePath(`products/${handle}?sections=product-info-json`));
            if (response.ok) {
              const sectionsData = await response.json();
              const html = sectionsData["product-info-json"];
              if (html) {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = html;
                const productData = window._erj_?.getProductInfo?.(tempDiv);
                if (productData) {
                  config._productInfo = productData;
                }
              }
            }
          } catch (e) {
            console.error(`Failed to fetch product info for ${handle}:`, e);
          }
        }
      }
      return config;
    });
    return Promise.all(fetchPromises);
  }

  function groupConfigs(configs) {
    const groups = {
      "time-limited": [],
      default: [],
      gold: [],
      diamond: [],
      ambassador: [],
    };
    const ALL_LEVELS = ["silver", "gold", "diamond", "ambassador"];

    configs.forEach((config) => {
      if (config.validFrom || config.validTo) {
        groups["time-limited"].push(config);
        return;
      }

      const levels = (config.eligibleVipLevels || []).map((l) => l.toLowerCase());
      const hasAllLevels = ALL_LEVELS.every((level) => levels.includes(level));

      if (hasAllLevels || levels.length === 0) {
        groups.default.push(config);
      } else {
        const lowestLevel = ALL_LEVELS.find((level) => levels.includes(level));
        if (lowestLevel === "silver" || !lowestLevel) {
          groups.default.push(config);
        } else if (groups[lowestLevel]) {
          groups[lowestLevel].push(config);
        }
      }
    });

    return groups;
  }

  function renderGroupedList(container, grouped, navLabels) {
    const ORDER = ["time-limited", "default", "gold", "diamond", "ambassador"];

    const localeAccount = window._erj_?.getGlobalLocaleStrings()?.account || {};
    const { zone: zoneLocale, time_limited: timeLimitedLocale, monthly_exclusives: monthlyExclusivesLocale } = localeAccount;

    const translations = {
      alwaysOn: localeAccount.always_on_rewards || "Always-on",
      zone: zoneLocale || "Zone",
      timeLimited: timeLimitedLocale || "Limited-Time",
      monthlyExclusives: monthlyExclusivesLocale || {},
    };

    const getMonthlyExclusiveText = () => {
      const monthKeys = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
      const currentMonthIndex = new Date().getMonth();
      const currentMonthKey = monthKeys[currentMonthIndex];
      return translations.monthlyExclusives[currentMonthKey] || `${currentMonthKey} Exclusive`;
    };

    const groupLabel = (levelKey) => {
      if (levelKey === "time-limited") return translations.timeLimited;
      if (levelKey === "default") return translations.alwaysOn;
      return localeAccount.membership?.benefits?.values?.redeem_points?.[levelKey] || `${localeAccount.membership?.tiers?.[levelKey] || levelKey} ${translations.zone}`;
    };

    const groups = ORDER.filter((levelKey) => (grouped[levelKey] || []).length > 0);
    if (!groups.length) {
      container.replaceChildren(Object.assign(document.createElement("div"), { className: "redeem-list__empty", textContent: window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.rewards_empty || "No rewards available at the moment." }));
      return;
    }

    const mobileQuery = window.matchMedia("(max-width: 767px), (max-width: 1024px) and (orientation: portrait)");
    if (!container._redeemLayoutListener) {
      container._redeemLayoutListener = () => renderGroupedList(container, grouped, navLabels);
      mobileQuery.addEventListener("change", container._redeemLayoutListener);
    }
    const rows = mobileQuery.matches
      ? Array.from({ length: Math.ceil(groups.length / 2) }, (_, i) => groups.slice(i * 2, i * 2 + 2))
      : [groups];

    container.innerHTML = rows.map((row) => `
      <div class="redeem-group">
      <div class="redeem-tabs" role="tablist">
        ${row
          .map(
            (levelKey, i) => `
          <button type="button" role="tab" class="redeem-tab${i === 0 ? " is-active" : ""}" data-rd-tab="${levelKey}" aria-selected="${i === 0}">
            <span class="redeem-tab__label">${groupLabel(levelKey)}</span>
          </button>`
          )
          .join("")}
      </div>
      ${row
        .map(
          (levelKey, i) => `
        <div class="redeem-panel" data-rd-panel="${levelKey}" role="tabpanel" ${i === 0 ? "" : "hidden"}>
          <div class="redeem-slider-container">
            <redeem-slider class="redeem-slider">
              <div class="redeem-track" data-rd-track>
                ${grouped[levelKey].map((item) => renderRedeemItem(item)).join("")}
              </div>
              <button type="button" class="redeem-nav redeem-nav-prev" data-rd-prev aria-label="${navLabels.prev}"></button>
              <button type="button" class="redeem-nav redeem-nav-next" data-rd-next aria-label="${navLabels.next}"></button>
            </redeem-slider>
          </div>
        </div>`
        )
        .join("")}
      </div>
    `).join("");
  }

  function onTabClick(e) {
    const tab = e.target.closest("[data-rd-tab]");
    if (!tab || tab.classList.contains("is-active")) return;

    const root = tab.closest(".redeem-group");
    root.querySelectorAll("[data-rd-tab]").forEach((t) => {
      const active = t === tab;
      t.classList.toggle("is-active", active);
      t.setAttribute("aria-selected", String(active));
    });
    root.querySelectorAll("[data-rd-panel]").forEach((p) => {
      p.hidden = p.dataset.rdPanel !== tab.dataset.rdTab;
    });
    // The slider measured itself while its panel was hidden (width 0); re-measure.
    root.querySelector(`[data-rd-panel="${tab.dataset.rdTab}"] redeem-slider`)?.updateButtonState();
  }

  function onSwatchClick(e) {
    const swatch = e.target.closest(".redeem-item__swatch");
    if (!swatch || swatch.classList.contains("is-active")) return;

    const container = swatch.closest(".redeem-item__swatches");
    const item = swatch.closest(".redeem-item");
    const img = item.querySelector(".redeem-item__image--default");
    const hoverImg = item.querySelector(".redeem-item__image--hover");
    const btn = item.querySelector('[data-action="redeem"]');
    const btnText = btn?.querySelector(".redeem-item__btn-text");
    const overlay = item.querySelector(".redeem-item__image-overlay");

    const newImage = swatch.dataset.image;
    const newModelImage = swatch.dataset.modelImage;
    const isAvailable = swatch.dataset.available === "true";
    const canRedeemBase = btn.dataset.canRedeemBase === "true";
    const cannotRedeemReason = btn.dataset.cannotRedeemReason || "";
    const localeStrings = window._erj_?.getGlobalLocaleStrings()?.account || {};
    const isLoggedIn = Boolean(window._erj_?.customer?.id);

    // Update active state
    container.querySelectorAll(".redeem-item__swatch").forEach((s) => s.classList.remove("is-active"));
    swatch.classList.add("is-active");

    // Update image
    if (img && newImage) {
      img.src = newImage;
    }
    if (hoverImg && newModelImage) {
      hoverImg.src = newModelImage;
    }

    // Update availability UI
    if (overlay) {
      if (isAvailable) {
        overlay.style.display = "none";
      } else {
        overlay.style.display = "flex";
      }
    }
    if (btn && btnText) {
      let finalButtonText = "";
      let isBtnDisabled = false;

      if (!isAvailable) {
        isBtnDisabled = true;
        finalButtonText = localeStrings.out_of_stock || "Out of Stock";
      } else if (!isLoggedIn) {
        isBtnDisabled = true;
        finalButtonText = localeStrings.log_in_to_redeem || "Log in to Redeem";
      } else if (canRedeemBase) {
        isBtnDisabled = false;
        finalButtonText = localeStrings.redeem_now || "Redeem Now";
      } else {
        isBtnDisabled = true;
        const reasonMap = {
          level_mismatch: localeStrings.level_mismatch || "VIP level not eligible",
          frequency_limit: localeStrings.frequency_limit || "Reached Limit",
          need_more_points: localeStrings.need_more_points || "More Points Needed",
        };
        finalButtonText = reasonMap[cannotRedeemReason] || localeStrings.need_more_points || "More Points Needed";
      }

      btn.disabled = isBtnDisabled;
      btnText.textContent = finalButtonText;

      if (isBtnDisabled) {
        btn.classList.add("redeem-item__btn--disabled");
        btn.classList.remove("redeem-item__btn--active");
      } else {
        btn.classList.remove("redeem-item__btn--disabled");
        btn.classList.add("redeem-item__btn--active");
      }
    }
  }

  function renderRedeemItem(config) {
    let { id, name, requiredPoints, type, thumbnailUrl, canRedeem, cannotRedeemReason, configExtra, _productInfo } = config;
    name = window._erj_.esc(_productInfo?.title || window._erj_?.localizedRewardName?.(config) || name);
    const canRedeemBase = canRedeem;
    let productLink = "",
      productHandle;

    let currentThumbnail = thumbnailUrl;
    let currentModelImage = _productInfo?.media?.[1]?.src;
    let swatchesHtml = "";
    let isSelectedVariantAvailable = true;

    if (_productInfo && _productInfo.options) {
      const colorOption = _productInfo.options.find((opt) => opt.name.toLowerCase().includes("color") || opt.values.some((v) => v.swatch?.color || v.swatch?.image));

      if (colorOption) {
        const defaultValue = colorOption.values.find((v) => v.available) || colorOption.values[0];

        if (defaultValue) {
          const variant = _productInfo.variants.find((v) => v.options[colorOption.position - 1] === defaultValue.name);
          if (variant && variant.image) {
            currentThumbnail = variant.image;
          }
          if (variant && variant.variantModelImage) {
            currentModelImage = variant.variantModelImage;
          }
          isSelectedVariantAvailable = defaultValue.available;

          swatchesHtml = `
            <div class="redeem-item__swatches" data-option-position="${colorOption.position}">
              ${colorOption.values
                .map((val) => {
                  const isActive = val.name === defaultValue.name;
                  const swatch = val.swatch || {};
                  const variantForVal = _productInfo.variants.find((v) => v.options[colorOption.position - 1] === val.name);
                  const imgUrl = variantForVal?.image || "";
                  const modelImgUrl = variantForVal?.variantModelImage || "";

                  return `
                  <button 
                    class="redeem-item__swatch ${isActive ? "is-active" : ""} ${!val.available ? "is-unavailable" : ""}"
                    data-value="${val.name}"
                    data-image="${imgUrl}"
                    data-model-image="${modelImgUrl}"
                    data-available="${val.available}"
                    title="${val.name}"
                    type="button"
                  >
                    ${
                      swatch.image
                        ? `<img src="${swatch.image}" alt="${val.name}">`
                        : swatch.color
                          ? `<span style="background-color: ${swatch.color}; width: 100%; height: 100%; display: block;"></span>`
                          : val.name
                    }
                  </button>
                `;
                })
                .join("")}
            </div>
          `;
        }
      } else {
        // No color option, check overall product availability
        isSelectedVariantAvailable = _productInfo.available !== false;
      }
    }

    const isProductOutOfStock = _productInfo && _productInfo.available === false;

    if (configExtra?.type === "free_product") {
      productHandle = configExtra?.products?.[0]?.handle || "";
      productLink = window._erj_.localePath(`products/${productHandle}`);
    }

    const localeStrings = window._erj_?.getGlobalLocaleStrings()?.account || {};
    const isLoggedIn = Boolean(window._erj_?.customer?.id);

    let buttonText = "";
    if (!isSelectedVariantAvailable) {
      canRedeem = false;
      buttonText = localeStrings.out_of_stock || "Out of Stock";
    } else if (!isLoggedIn) {
      buttonText = localeStrings.log_in_to_redeem || "Log in to Redeem";
    } else if (canRedeem) {
      buttonText = localeStrings.redeem_now || "Redeem Now";
    } else {
      const reasonMap = {
        level_mismatch: localeStrings.level_mismatch || "VIP level not eligible",
        frequency_limit: localeStrings.frequency_limit || "Reached Limit",
        need_more_points: localeStrings.need_more_points || "More Points Needed",
      };
      buttonText = reasonMap[cannotRedeemReason] || localeStrings.need_more_points || "More Points Needed";
    }

    const isButtonDisabled = !isSelectedVariantAvailable || !isLoggedIn || !canRedeem;
    const buttonClass = isButtonDisabled ? "redeem-item__btn--disabled" : "redeem-item__btn--active";

    return `
      <div class="redeem-item" data-rd-slide>
        <div class="redeem-item__image-wrapper">
        ${
          productLink
            ? `
          <a href="${productLink}">
            <img class="redeem-item__image redeem-item__image--default" src="${currentThumbnail}" alt="${name}" loading="lazy">
            ${currentModelImage ? `<img class='redeem-item__image redeem-item__image--hover' src='${currentModelImage}' alt='${name}' decoding="async">` : ""}
            <div class="redeem-item__image-overlay" style="display: ${isSelectedVariantAvailable ? "none" : "flex"}"></div>
          </a>`
            : `<img class="redeem-item__image redeem-item__image--default" src="${currentThumbnail}" alt="${name}" loading="lazy">
             ${currentModelImage ? `<img class='redeem-item__image redeem-item__image--hover' src='${currentModelImage}' alt='${name}' decoding="async">` : ""}
            <div class="redeem-item__image-overlay" style="display: ${isSelectedVariantAvailable ? "none" : "flex"}"></div>`
        }
        <style>
          .redeem-item__image-wrapper .redeem-item__image--hover { display: none; }
          .redeem-item__image-wrapper:has(.redeem-item__image--hover):hover .redeem-item__image--default { display: none; }
          .redeem-item__image-wrapper:has(.redeem-item__image--hover):hover .redeem-item__image--hover { display: block; }
        </style>
        
        ${swatchesHtml}
        </div>
        
        <div class="redeem-item__info">
          <div class="redeem-item__points">
            ${window._erj_.esc((localeStrings.page_copy?.points_for_free || "{{ points }} points for free").replace("{{ points }}", "__points__")).replace("__points__", `<span class="redeem-item__points-number">${window._erj_.esc(requiredPoints)}</span>`)}
          </div>
          ${productLink ? `<a class="redeem-item__name" href="${productLink}" target="_blank">${name}</a>` : `<div class="redeem-item__name">${name}</div>`}
          
          <button 
            class="redeem-item__btn ${buttonClass}" 
            data-id="${id}" 
            data-action="redeem"
            data-can-redeem-base="${canRedeemBase}"
            data-cannot-redeem-reason="${cannotRedeemReason || ""}"
            ${isButtonDisabled ? "disabled" : ""}
          >
            <span class="redeem-item__btn-text">${buttonText}</span>
          </button>
        </div>
      </div>
    `;
  }

  async function onRedeemClick(e) {
    const btn = e.target.closest('[data-action="redeem"]');
    if (!btn || btn.disabled || btn.classList.contains("is-loading")) return;

    const codeConfigId = btn.dataset.id;
    const customerId = window._erj_?.customerId || window.ShopifyAnalytics?.meta?.page?.customerId || window.hvgUserId;

    if (!customerId) {
      alert((window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.login_redeem || "Please log in to redeem rewards."));
      return;
    }

    btn.classList.add("is-loading");
    const originalText = btn.querySelector(".redeem-item__btn-text").textContent;
    btn.querySelector(".redeem-item__btn-text").textContent = (window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.redeeming || "Redeeming...");

    try {
      const response = await fetch("https://api.enroutejewelry.com/loyalty/redeem", {
        method: "POST",
        // headers: {
        //   "Content-Type": "application/json",
        // },
        body: JSON.stringify({
          customerId: String(customerId),
          codeConfigId: codeConfigId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        window._erj_?.clearAccountInfo?.();
        window.location.reload();
      } else {
        console.error("Reward redemption failed:", result.message);
        const errorMsg = (window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.redeem_error || "Redeem error, please try later");
        alert(errorMsg);
      }
    } catch (error) {
      console.error("Redeem Error:", error);
      alert((window._erj_?.getGlobalLocaleStrings?.()?.account?.page_copy?.redeem_error || "Redeem error, please try later"));
    } finally {
      btn.classList.remove("is-loading");
      btn.querySelector(".redeem-item__btn-text").textContent = originalText;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
